import { useState } from "react";
import { money } from "../lib/api";
import { ReceiptFooter, isFullyRefunded } from "./ReceiptFooter";
import {
  CurrencyDollar,
  CreditCard,
  Globe,
  CheckCircle,
  Clock,
  Warning,
  Receipt,
} from "@phosphor-icons/react";

const METHOD_ICONS = {
  cash: CurrencyDollar,
  tyro: CreditCard,
  card: CreditCard,
  card_manual: CreditCard,
  stripe: Globe,
  other: Receipt,
};

const METHOD_LABELS = {
  cash: "Cash",
  tyro: "Tyro EFTPOS",
  card: "Card",
  card_manual: "Card",
  stripe: "Stripe",
  other: "Other",
};

/**
 * Payment summary for tickets paid at POS (no online invoice on the ticket).
 * Styled to match the modern LinkedInvoiceSection card so every ticket — old
 * POS sales and new online invoices alike — shows one consistent summary.
 */
export default function PaymentSummary({ ticket, linkedSales, totalLabel = "Total" }) {
  const activeSales = (linkedSales?.sales || []).filter((s) => !s.voided);
  const voidedSales = (linkedSales?.sales || []).filter((s) => s.voided);

  // Fallback card when there's no sale yet but the ticket has a price.
  const fallbackTotal = Number(ticket?.final_cost || ticket?.estimated_cost || 0);

  return (
    <div className="space-y-3" data-testid="payment-summary">
      {activeSales.length > 0 ? (
        activeSales.map((sale) => <SaleCard key={sale.id} sale={sale} totalLabel={totalLabel} />)
      ) : fallbackTotal > 0 ? (
        <UnpaidCard total={fallbackTotal} totalLabel={totalLabel} />
      ) : null}

      {linkedSales?.is_duplicate && activeSales.length > 1 && (
        <div className="flex items-start gap-2 text-[12px] bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl" data-testid="duplicate-warning">
          <Warning size={16} weight="fill" className="text-red-500 shrink-0 mt-0.5" />
          <div>
            <span className="font-bold">Duplicate billing detected.</span>{" "}
            This ticket has {linkedSales.active_count} active invoices. Review and void one if it shouldn't exist.
          </div>
        </div>
      )}

      {voidedSales.length > 0 && <VoidedSalesSection sales={voidedSales} />}
    </div>
  );
}

function SaleCard({ sale, totalLabel }) {
  const total = Number(sale.total || 0);
  const payments = (sale.payments || []).filter((p) => Number(p.amount) > 0 && !p.corrected_at && !p.is_refund)
    .map((p) => ({ method: p.method || "cash", amount: Number(p.amount), tyro: p.method === "tyro" || !!p.tyro_meta }));
  const paid = payments.reduce((s, p) => s + p.amount, 0);
  const balance = sale.balance_due != null ? Number(sale.balance_due) : Math.max(0, total - paid);
  const refunded = Number(sale.refund_total || 0);
  const fullRefund = isFullyRefunded(total, refunded);
  const overpaid = Math.round(Math.max(0, paid - refunded - total) * 100) / 100;
  return (
    <ReceiptFooter testId={`sale-summary-${sale.sale_number}`} label={totalLabel} total={total} payments={payments}
                   balance={balance} overpaid={overpaid} number={sale.sale_number} refunded={refunded} fullyRefunded={fullRefund}
                   statusLabel={fullRefund ? "Refunded" : refunded > 0.005 ? "Part refunded" : overpaid > 0.005 ? "Overpaid" : balance < 0.01 ? "Paid" : paid > 0.005 ? "Partly paid" : "Unpaid"} />
  );
}

function UnpaidCard({ total, totalLabel }) {
  return (
    <ReceiptFooter testId="payment-summary-unpaid" label={totalLabel} total={total}
                   statusLabel="Charge at POS or send an invoice" />
  );
}

function VoidedSalesSection({ sales }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="text-[11px] text-zinc-400 hover:text-zinc-600 font-semibold uppercase tracking-wider"
      >
        {open ? "Hide" : "Show"} {sales.length} voided invoice{sales.length !== 1 ? "s" : ""}
      </button>
      {open && (
        <div className="mt-2 space-y-1">
          {sales.map((s) => (
            <div
              key={s.id}
              className="flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-50 border border-zinc-100 opacity-60"
            >
              <div className="flex items-center gap-2 text-[12px]">
                <span className="font-mono font-semibold text-zinc-500">{s.sale_number}</span>
                <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600">VOIDED</span>
                <span className="text-zinc-400">{s.void_reason || ""}</span>
              </div>
              <span className="font-mono text-[12px] text-zinc-400 line-through">${money(s.total)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
